import { useRef, useState } from 'react'

/** Resize the left-hand table while keeping the chat usable. */
export default function TablePanelResizeHandle({ width, onResize, className = '' }: { width: number; onResize: (width: number) => void; className?: string }) {
  const drag = useRef<{ pointerId: number; x: number; width: number; containerWidth: number } | null>(null)
  const [dragging, setDragging] = useState(false)
  const resize = (next: number) => onResize(Math.max(30, Math.min(75, next)))

  return (
    <div
      role="separator"
      aria-label="Table panel width"
      aria-orientation="vertical"
      aria-valuemin={30}
      aria-valuemax={75}
      aria-valuenow={Math.round(width)}
      aria-valuetext={`${Math.round(width)} percent`}
      tabIndex={0}
      className={`${className} relative z-20 w-4 shrink-0 cursor-col-resize touch-none select-none outline-hidden hover:bg-brand-neutral-200 focus-visible:bg-brand-neutral-200 ${
        dragging ? 'bg-brand-neutral-200' : ''
      }`}
      onPointerDown={(event) => {
        if (event.button !== 0 || !event.isPrimary) return
        const containerWidth = event.currentTarget.parentElement?.getBoundingClientRect().width
        if (!containerWidth) return
        event.preventDefault()
        event.currentTarget.focus()
        event.currentTarget.setPointerCapture(event.pointerId)
        drag.current = { pointerId: event.pointerId, x: event.clientX, width, containerWidth }
        setDragging(true)
      }}
      onPointerMove={(event) => {
        const start = drag.current
        if (!start || start.pointerId !== event.pointerId) return
        resize(start.width + ((event.clientX - start.x) / start.containerWidth) * 100)
      }}
      onPointerUp={(event) => {
        if (drag.current?.pointerId !== event.pointerId) return
        event.currentTarget.releasePointerCapture(event.pointerId)
        drag.current = null
        setDragging(false)
      }}
      onPointerCancel={() => {
        drag.current = null
        setDragging(false)
      }}
      onLostPointerCapture={() => {
        drag.current = null
        setDragging(false)
      }}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 5 : 1
        if (event.key === 'ArrowLeft') resize(width - step)
        else if (event.key === 'ArrowRight') resize(width + step)
        else if (event.key === 'Home') resize(30)
        else if (event.key === 'End') resize(75)
        else return
        event.preventDefault()
      }}
    />
  )
}
